/**
 * Pure ingredient scaling & unit conversion core (ROADMAP §2 Phase 1 Recipes — servings
 * control + units toggle on the detail page, and future AI/meal-planner reuse). No
 * dependencies, no DOM/Yjs — just numbers and strings in, strings out, so it can run
 * anywhere `@awwyeah/shared` is imported.
 *
 * Nothing here ever needs to touch `Ingredient.text`: callers keep the raw stored line as
 * the source of truth and only ever render the output of these functions as a *view*.
 */
// ---------------------------------------------------------------------------------------
// Unit table — recognized spellings/abbreviations normalize to one canonical key each.
// ---------------------------------------------------------------------------------------
const VOLUME_BASE_ML = {
    tsp: 4.92892,
    tbsp: 14.7868,
    floz: 29.5735,
    cup: 236.588,
    pint: 473.176,
    quart: 946.353,
    gallon: 3785.41,
    ml: 1,
    l: 1000,
};
const WEIGHT_BASE_G = {
    oz: 28.3495,
    lb: 453.592,
    g: 1,
    kg: 1000,
};
const UNIT_TABLE = {};
for (const [canonical, toBase] of Object.entries(VOLUME_BASE_ML)) {
    UNIT_TABLE[canonical] = {
        canonical,
        category: 'volume',
        system: canonical === 'ml' || canonical === 'l' ? 'metric' : 'us',
        toBase,
    };
}
for (const [canonical, toBase] of Object.entries(WEIGHT_BASE_G)) {
    UNIT_TABLE[canonical] = {
        canonical,
        category: 'weight',
        system: canonical === 'g' || canonical === 'kg' ? 'metric' : 'us',
        toBase,
    };
}
/** Alias (lowercased, trailing "." stripped) → canonical key. */
const UNIT_ALIASES = {
    tsp: 'tsp',
    ts: 'tsp',
    teaspoon: 'tsp',
    teaspoons: 'tsp',
    tbsp: 'tbsp',
    tbsps: 'tbsp',
    tbs: 'tbsp',
    tb: 'tbsp',
    tablespoon: 'tbsp',
    tablespoons: 'tbsp',
    'fl oz': 'floz',
    floz: 'floz',
    'fluid ounce': 'floz',
    'fluid ounces': 'floz',
    cup: 'cup',
    cups: 'cup',
    c: 'cup',
    pt: 'pint',
    pint: 'pint',
    pints: 'pint',
    qt: 'quart',
    quart: 'quart',
    quarts: 'quart',
    gal: 'gallon',
    gallon: 'gallon',
    gallons: 'gallon',
    ml: 'ml',
    milliliter: 'ml',
    milliliters: 'ml',
    millilitre: 'ml',
    millilitres: 'ml',
    l: 'l',
    liter: 'l',
    liters: 'l',
    litre: 'l',
    litres: 'l',
    oz: 'oz',
    ounce: 'oz',
    ounces: 'oz',
    lb: 'lb',
    lbs: 'lb',
    pound: 'lb',
    pounds: 'lb',
    g: 'g',
    gram: 'g',
    grams: 'g',
    kg: 'kg',
    kilogram: 'kg',
    kilograms: 'kg',
};
const UNIT_DISPLAY = {
    tsp: { singular: 'tsp', plural: 'tsp' },
    tbsp: { singular: 'tbsp', plural: 'tbsp' },
    floz: { singular: 'fl oz', plural: 'fl oz' },
    cup: { singular: 'cup', plural: 'cups' },
    pint: { singular: 'pint', plural: 'pints' },
    quart: { singular: 'quart', plural: 'quarts' },
    gallon: { singular: 'gallon', plural: 'gallons' },
    ml: { singular: 'ml', plural: 'ml' },
    l: { singular: 'L', plural: 'L' },
    oz: { singular: 'oz', plural: 'oz' },
    lb: { singular: 'lb', plural: 'lbs' },
    g: { singular: 'g', plural: 'g' },
    kg: { singular: 'kg', plural: 'kg' },
};
/**
 * Normalizes a free-text unit ("Tbsp", "tablespoons", "c.") to its canonical key, or `null`
 * for anything not in the table (pinch, clove, can, "to taste", ...) — those are left
 * completely untouched by the rest of this module, on purpose.
 *
 * Case matters for the two single-letter kitchen abbreviations: `T` = tablespoon,
 * `t` = teaspoon (the one place where recipe convention is case-sensitive).
 */
export function normalizeUnit(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    if (trimmed === 'T')
        return 'tbsp';
    if (trimmed === 't')
        return 'tsp';
    const cleaned = trimmed.toLowerCase().replace(/\.+$/, '');
    return UNIT_ALIASES[cleaned] ?? null;
}
function isApproxOne(value) {
    return Math.abs(value - 1) < 1e-6;
}
function formatUnitDisplay(canonical, value) {
    const entry = UNIT_DISPLAY[canonical];
    if (!entry)
        return canonical;
    return isApproxOne(value) ? entry.singular : entry.plural;
}
// ---------------------------------------------------------------------------------------
// Quantity parsing — unicode fractions in, kitchen-sane fractions out.
// ---------------------------------------------------------------------------------------
const UNICODE_FRACTION_VALUES = {
    '¼': 1 / 4,
    '½': 1 / 2,
    '¾': 3 / 4,
    '⅓': 1 / 3,
    '⅔': 2 / 3,
    '⅕': 1 / 5,
    '⅖': 2 / 5,
    '⅗': 3 / 5,
    '⅘': 4 / 5,
    '⅙': 1 / 6,
    '⅚': 5 / 6,
    '⅛': 1 / 8,
    '⅜': 3 / 8,
    '⅝': 5 / 8,
    '⅞': 7 / 8,
};
const UNICODE_FRACTION_CHARS = Object.keys(UNICODE_FRACTION_VALUES).join('');
const GLUED_FRACTION_RE = new RegExp(`^(\\d+)([${UNICODE_FRACTION_CHARS}])$`);
const SIMPLE_FRACTION_RE = /^(\d+)\/(\d+)$/;
const DECIMAL_RE = /^\d+(?:\.\d+)?$/;
/** Parses a single quantity token ("2", "1/2", "½", "1½", "1.5") to a number, or `null`. */
function parseQuantityToken(token) {
    const t = token.trim();
    if (!t)
        return null;
    const unicode = UNICODE_FRACTION_VALUES[t];
    if (unicode !== undefined)
        return unicode;
    const glued = GLUED_FRACTION_RE.exec(t);
    if (glued) {
        const [, whole, frac] = glued;
        return Number(whole) + UNICODE_FRACTION_VALUES[frac];
    }
    const simple = SIMPLE_FRACTION_RE.exec(t);
    if (simple) {
        const [, num, den] = simple;
        const denominator = Number(den);
        if (denominator === 0)
            return null;
        return Number(num) / denominator;
    }
    if (DECIMAL_RE.test(t))
        return Number(t);
    return null;
}
/** Parses a whole quantity value, e.g. "1 1/2" (space-separated whole + fraction). */
function parseQuantityValue(text) {
    const parts = text.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return null;
    let total = 0;
    for (const part of parts) {
        const value = parseQuantityToken(part);
        if (value === null)
            return null;
        total += value;
    }
    return total;
}
/** Parses "2", "1 1/2", "¾", or a range like "2-3" / "2 - 3". `null` = unparseable. */
function parseQuantityRange(text) {
    const trimmed = text.trim();
    const rangeMatch = /^(.+?)\s*[-–]\s*(.+)$/.exec(trimmed);
    if (rangeMatch) {
        const [, lowStr, highStr] = rangeMatch;
        const low = parseQuantityValue(lowStr);
        const high = parseQuantityValue(highStr);
        if (low !== null && high !== null)
            return { low, high };
    }
    const value = parseQuantityValue(trimmed);
    if (value === null)
        return null;
    return { low: value, high: null };
}
/** Kitchen fractions considered "sensible" for amounts under 4, nearest-match snapped. */
const KITCHEN_FRACTIONS = [
    { value: 1 / 8, glyph: '⅛' },
    { value: 1 / 6, glyph: '⅙' },
    { value: 1 / 4, glyph: '¼' },
    { value: 1 / 3, glyph: '⅓' },
    { value: 3 / 8, glyph: '⅜' },
    { value: 1 / 2, glyph: '½' },
    { value: 5 / 8, glyph: '⅝' },
    { value: 2 / 3, glyph: '⅔' },
    { value: 3 / 4, glyph: '¾' },
    { value: 5 / 6, glyph: '⅚' },
    { value: 7 / 8, glyph: '⅞' },
];
/** Snaps a 0..1 fractional remainder to the nearest kitchen fraction (or 0 / carry to 1). */
function nearestKitchenFraction(frac) {
    const candidates = [
        { value: 0 },
        ...KITCHEN_FRACTIONS,
        { value: 1 },
    ];
    let best = candidates[0];
    let bestDist = Math.abs(frac - best.value);
    for (const c of candidates) {
        const dist = Math.abs(frac - c.value);
        if (dist < bestDist) {
            best = c;
            bestDist = dist;
        }
    }
    if (best.value === 0)
        return 'none';
    if (best.value === 1)
        return 'carry';
    return { glyph: best.glyph };
}
/**
 * Formats a positive number as a kitchen-sane display string: unicode fractions for small
 * amounts (nearest eighth/sixth/third — never "1.3333"), halves for mid amounts, whole
 * numbers once amounts get large enough that fractions stop being useful.
 */
export function formatQuantity(value) {
    if (!Number.isFinite(value) || value <= 0)
        return '0';
    if (value >= 10) {
        return String(Math.round(value));
    }
    if (value >= 4) {
        const rounded = Math.round(value * 2) / 2;
        const whole = Math.floor(rounded);
        const half = rounded - whole >= 0.5 - 1e-9;
        return half ? `${whole}½` : String(whole);
    }
    const whole = Math.floor(value);
    const frac = value - whole;
    const snapped = nearestKitchenFraction(frac);
    if (snapped === 'none')
        return whole === 0 ? '0' : String(whole);
    if (snapped === 'carry')
        return String(whole + 1);
    return whole === 0 ? snapped.glyph : `${whole}${snapped.glyph}`;
}
/**
 * Scales a raw quantity string ("2", "1 1/2", "¾", "2-3") by `factor`, returning a
 * kitchen-sane display string. Ranges scale both ends. Anything unparseable (blank,
 * "to taste" text that slipped into the field, ...) is returned unchanged.
 */
export function scaleQuantity(quantity, factor) {
    const trimmed = quantity.trim();
    if (!trimmed)
        return trimmed;
    const range = parseQuantityRange(trimmed);
    if (!range)
        return trimmed;
    const low = formatQuantity(range.low * factor);
    if (range.high === null)
        return low;
    const high = formatQuantity(range.high * factor);
    return `${low}–${high}`;
}
// ---------------------------------------------------------------------------------------
// Unit conversion with sensible auto step-up/step-down.
// ---------------------------------------------------------------------------------------
/** Ascending ladders used to pick the "nicest" unit for a converted value. */
const VOLUME_US_LADDER = ['tsp', 'tbsp', 'cup', 'pint', 'quart', 'gallon'];
const VOLUME_METRIC_LADDER = ['ml', 'l'];
const WEIGHT_US_LADDER = ['oz', 'lb'];
const WEIGHT_METRIC_LADDER = ['g', 'kg'];
/** Picks the largest unit on the ladder whose value is still >= 1 (else the smallest). */
function pickBestUnit(baseValue, ladder, base) {
    let chosen = ladder[0];
    for (const unit of ladder) {
        const v = baseValue / base[unit];
        if (v >= 1 - 1e-9)
            chosen = unit;
        else
            break;
    }
    return chosen;
}
/**
 * Converts `value unitRaw` to the nicest unit within `toSystem`, auto stepping (1000ml →
 * 1L, 0.25 cup → 4 tbsp). Returns `null` for unrecognized units — callers should leave
 * those completely untouched.
 */
export function convertQuantity(value, unitRaw, toSystem) {
    const canonical = normalizeUnit(unitRaw);
    if (!canonical)
        return null;
    const info = UNIT_TABLE[canonical];
    if (!info)
        return null;
    const baseValue = value * info.toBase;
    if (info.category === 'volume') {
        const ladder = toSystem === 'metric' ? VOLUME_METRIC_LADDER : VOLUME_US_LADDER;
        const unit = pickBestUnit(baseValue, ladder, VOLUME_BASE_ML);
        return { quantity: baseValue / VOLUME_BASE_ML[unit], unit };
    }
    const ladder = toSystem === 'metric' ? WEIGHT_METRIC_LADDER : WEIGHT_US_LADDER;
    const unit = pickBestUnit(baseValue, ladder, WEIGHT_BASE_G);
    return { quantity: baseValue / WEIGHT_BASE_G[unit], unit };
}
// ---------------------------------------------------------------------------------------
// Optional volume ↔ weight via a small honest density table — always marked "≈".
// ---------------------------------------------------------------------------------------
/** Grams per US cup. Home-kitchen approximations, not lab measurements — hence "≈". */
const DENSITY_G_PER_CUP = {
    'powdered sugar': 120,
    'brown sugar': 220,
    flour: 120,
    sugar: 200,
    butter: 227,
    rice: 185,
    oats: 90,
    cocoa: 90,
};
/** Longest keys first so "brown sugar" matches before the bare "sugar". */
const DENSITY_KEYS = Object.keys(DENSITY_G_PER_CUP).sort((a, b) => b.length - a.length);
function matchDensityKey(ingredientName) {
    const lower = ingredientName.toLowerCase();
    for (const key of DENSITY_KEYS) {
        if (lower.includes(key))
            return key;
    }
    return null;
}
/**
 * Tries a density-based volume↔weight conversion for a known dry good (flour, sugar,
 * butter, ...) when a plain unit conversion would land on the "wrong" measuring tool for
 * the target system (metric bakers weigh flour; US bakers scoop it). Returns `null` when
 * the ingredient name isn't in the density table or the unit isn't volume/weight.
 */
function tryDensityConversion(value, unitCanonical, ingredientName, toSystem) {
    const info = UNIT_TABLE[unitCanonical];
    if (!info || !ingredientName)
        return null;
    const densityKey = matchDensityKey(ingredientName);
    if (!densityKey)
        return null;
    const gPerCup = DENSITY_G_PER_CUP[densityKey];
    if (info.category === 'volume' && toSystem === 'metric') {
        const cups = (value * info.toBase) / VOLUME_BASE_ML.cup;
        const grams = cups * gPerCup;
        const unit = pickBestUnit(grams, WEIGHT_METRIC_LADDER, WEIGHT_BASE_G);
        return { quantity: grams / WEIGHT_BASE_G[unit], unit };
    }
    if (info.category === 'weight' && toSystem === 'us') {
        const grams = value * info.toBase;
        const cupsInMl = (grams / gPerCup) * VOLUME_BASE_ML.cup;
        const unit = pickBestUnit(cupsInMl, VOLUME_US_LADDER, VOLUME_BASE_ML);
        return { quantity: cupsInMl / VOLUME_BASE_ML[unit], unit };
    }
    return null;
}
// ---------------------------------------------------------------------------------------
// Ingredient-level display transform — the one function the UI actually calls.
// ---------------------------------------------------------------------------------------
const NO_SPACE_UNITS = new Set(['g', 'kg', 'ml', 'l']);
/**
 * Scales `ingredient` by `factor` and optionally converts its unit to `toSystem`, returning
 * a display-only string. Never mutates the input. Ingredients with no parsed `quantity`
 * (or a quantity/unit our parser can't make sense of) are returned with their original
 * `text`, byte for byte — "to taste", "pinch of salt", weird hand-typed lines, all safe.
 */
export function scaleIngredientForDisplay(ingredient, factor, toSystem) {
    const needsScale = Math.abs(factor - 1) > 1e-9;
    const needsConvert = toSystem !== null;
    if (!needsScale && !needsConvert)
        return { text: ingredient.text, approx: false };
    if (!ingredient.quantity)
        return { text: ingredient.text, approx: false };
    const range = parseQuantityRange(ingredient.quantity);
    if (!range)
        return { text: ingredient.text, approx: false };
    let low = range.low * factor;
    let high = range.high === null ? null : range.high * factor;
    const unitCanonical = ingredient.unit ? normalizeUnit(ingredient.unit) : null;
    let displayUnit = unitCanonical;
    let approx = false;
    if (needsConvert && unitCanonical) {
        const info = UNIT_TABLE[unitCanonical];
        if (info) {
            const density = tryDensityConversion(low, unitCanonical, ingredient.name, toSystem);
            if (density) {
                approx = true;
                const factorRatio = low === 0 ? 0 : density.quantity / low;
                displayUnit = density.unit;
                low = density.quantity;
                high = high === null ? null : high * factorRatio;
            }
            else if (info.system !== toSystem) {
                const convLow = convertQuantity(low, unitCanonical, toSystem);
                if (convLow) {
                    displayUnit = convLow.unit;
                    low = convLow.quantity;
                    if (high !== null) {
                        const convHigh = convertQuantity(high, unitCanonical, toSystem);
                        high = convHigh ? convHigh.quantity : high;
                    }
                }
            }
        }
    }
    const qtyText = high === null ? formatQuantity(low) : `${formatQuantity(low)}–${formatQuantity(high)}`;
    const unitText = displayUnit
        ? formatUnitDisplay(displayUnit, high ?? low)
        : ingredient.unit?.trim();
    const nameText = ingredient.name?.trim();
    // Metric mass/volume units read as "400g"/"250ml" with no space; everything else
    // ("4 tbsp", "2 lbs") keeps the space.
    const tightUnit = displayUnit !== null && NO_SPACE_UNITS.has(displayUnit);
    const qtyAndUnit = unitText
        ? tightUnit
            ? `${qtyText}${unitText}`
            : `${qtyText} ${unitText}`
        : qtyText;
    const parts = [qtyAndUnit, nameText].filter((p) => !!p && p.length > 0);
    const text = (approx ? '≈' : '') + parts.join(' ');
    return { text, approx };
}
//# sourceMappingURL=scaling.js.map